import { Header, type Job, Tab, TraitementLong } from '@mairistem/composants';
import { request } from '@mairistem/tools';
import _ from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';

import { FacturePrelevementContext, FactureRejetContext, LoadingContext } from '@/contexts';
import { postPrelevementsRejet, postPrelevementsValider, putFacture } from '@/routes';
import type { FactureListePrelevementItemType } from '@/types/facture';
import { formatMonetaire } from '@/utils';
import { extractApiErrorMessage } from '@/utils/apiErrors';
import { TabPaneHeader } from '../Autre';
import PrelevementTab from './PrelevementTab';

const GestionPrelevements = () => {
  // Sert à hard reset les prélèvements sélectionnés dans les onglets
  const [reset, setReset] = React.useState<boolean>(false);
  const [activeTab, setActiveTab] = React.useState<number>(0);
  // Identifiant du Job360 de génération des reçus, null quand aucun traitement n'est en cours
  const [job, setJob] = React.useState<number>(null);

  const {
    facturesLoadings: { factures: loading },
  } = React.useContext(LoadingContext);
  const {
    factures: prelevements,
    setFactures,
    setSearch,
    setPage,
    setTotal,
    applyDefaultFilters: applyDefaultFiltersPrelevements,
  } = React.useContext(FacturePrelevementContext);
  const { factures: rejets, applyDefaultFilters: applyDefaultFiltersRejets } =
    React.useContext(FactureRejetContext);

  const handleTabChange = React.useCallback(
    (e?: unknown, { activeIndex }: { activeIndex?: number } = {}) => {
      setReset(true);
      setSearch('');
      setPage(1);
      setTotal(0);
      applyDefaultFiltersPrelevements();
      applyDefaultFiltersRejets();
      setActiveTab(activeIndex);
    },
    [applyDefaultFiltersPrelevements, applyDefaultFiltersRejets, setTotal, setSearch, setPage],
  );

  const handleRejeterClick = React.useCallback(
    async (data: FactureListePrelevementItemType[], motif: string) => {
      try {
        await request.post(postPrelevementsRejet(), {
          identifiantsFactures: _.map(data, (r) => r.identifiant),
          motif,
        });

        // Pour trigger l'affichage de loading
        setFactures(null);
        // Simule un changement d'onglet pour refresh les données
        handleTabChange();

        return true;
      } catch (_error) {
        return false;
      }
    },
    [handleTabChange, setFactures],
  );

  // La génération des reçus est un traitement long côté API : la réponse n'est plus le
  // résultat mais un Job360 à suivre. Le refresh des listes et le toast de succès sont
  // donc déportés dans handleJobFinish — les déclencher ici annoncerait une génération
  // qui n'a pas encore eu lieu.
  const handleValiderClick = React.useCallback(async (data: FactureListePrelevementItemType[]) => {
    try {
      const response = (await request.post(postPrelevementsValider(), {
        identifiantsFactures: _.map(data, (r) => r.identifiant),
      })) as Job;

      setJob(response.identifiant);

      return true;
    } catch (error) {
      toast(extractApiErrorMessage(error), { type: 'error' });

      return false;
    }
  }, []);

  const handleJobFinish = React.useCallback(
    (finishedJob: Job) => {
      const { resultat, status } = finishedJob;

      setJob(null);

      if (status === -1) {
        toast(resultat || 'Une erreur est survenue lors de la génération des reçus.', {
          type: 'error',
        });

        return;
      }

      const { recusGeneres = 0, montantTotal = 0 } = JSON.parse(resultat || '{}');

      toast(
        recusGeneres === 1
          ? `1 reçu a été généré pour un montant total de ${formatMonetaire(montantTotal, 2, true)}`
          : `${recusGeneres} reçus ont été générés pour un montant total de ${formatMonetaire(montantTotal, 2, true)}`,
        { type: 'success' },
      );

      // Pour trigger l'affichage de loading
      setFactures(null);
      // Simule un changement d'onglet pour refresh les données
      handleTabChange();
    },
    [handleTabChange, setFactures],
  );

  const handleJobError = React.useCallback(() => {
    setJob(null);
    toast('Une erreur est survenue lors de la génération des reçus.', { type: 'error' });
  }, []);

  const handleRejetDelete = React.useCallback(
    async (rejet) => {
      try {
        const facture = _.find(rejets, { identifiant: rejet.identifiant });

        await request.put(putFacture(facture.identifiant), {
          ...facture,
          rejet: false,
          motifRejet: null,
          dateRejet: null,
        });

        // Pour trigger l'affichage de loading
        setFactures(null);
        // Simule un changement d'onglet pour refresh les données
        handleTabChange();

        return true;
      } catch (_error) {
        return false;
      }
    },
    [handleTabChange, setFactures, rejets],
  );

  React.useEffect(() => {
    let timeout;

    if (reset) {
      timeout = setTimeout(() => {
        setReset(false);
      }, 1000);
    }
    return () => clearTimeout(timeout);
  }, [reset]);

  const panes = [
    {
      menuItem: (
        <TabPaneHeader
          id="rejeter"
          name={
            <>
              Liste des factures prélevées
              <br />
              pour saisie des rejets
            </>
          }
          loading={activeTab === 0 && (loading || _.isNil(prelevements))}
        />
      ),
      render: () => (
        <PrelevementTab mode="rejeter" onRejeterClick={handleRejeterClick} reset={reset} />
      ),
    },
    {
      menuItem: (
        <TabPaneHeader
          id="rejets"
          name="Liste des rejets"
          loading={activeTab === 1 && (loading || _.isNil(rejets))}
        />
      ),
      render: () => (
        <PrelevementTab mode="rejets" onRejetDelete={handleRejetDelete} reset={reset} />
      ),
    },
    {
      menuItem: (
        <TabPaneHeader
          id="valider"
          name="Génération des reçus"
          loading={activeTab === 2 && (loading || _.isNil(rejets))}
        />
      ),
      render: () => (
        <PrelevementTab mode="valider" onValiderClick={handleValiderClick} reset={reset} />
      ),
    },
  ];

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Header as="h1" style={{ marginBottom: '1em' }}>
        <Header.Content>Gestion des factures prélevées</Header.Content>
      </Header>
      <Tab
        activeIndex={activeTab}
        menu={{
          secondary: true,
          pointing: true,
          size: 'big',
          style: { margin: 0 },
        }}
        panes={panes}
        onTabChange={handleTabChange}
      />
      {/* Montage conditionnel : TraitementLong est une modale bloquante dont l'intervalle
          de polling n'est pas nettoyé au démontage. `indeterminate` car la progression ne
          peut pas être publiée — la création des reçus tient dans une transaction unique,
          donc job_progression reste invisible du front jusqu'au commit. */}
      {!_.isNil(job) && (
        <TraitementLong
          title="Génération des reçus en cours"
          identifiantJob={job}
          indeterminate
          onFinish={handleJobFinish}
          onError={handleJobError}
          refreshInterval={3000}
        />
      )}
    </div>
  );
};

export default GestionPrelevements;
